import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import type { Tables, TablesUpdate } from '@/integrations/supabase/types'
import { useAuth } from './useAuth'

type Profile = Tables<'profiles'>
type ProfileUpdate = TablesUpdate<'profiles'>

export function useProfile() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()

      if (error) throw error
      return data as Profile | null
    },
    enabled: !!user?.id,
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (updates: ProfileUpdate) => {
      if (!user?.id) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] })
    },
  })
}

// Get dashboard stats
export function useDashboardStats() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['dashboard', 'stats', user?.id],
    queryFn: async () => {
      if (!user?.id) return null

      const today = new Date()
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      const todayStr = today.toISOString().split('T')[0]

      // Get today's hours
      const { data: todayEntries } = await supabase
        .from('time_entries')
        .select('duration')
        .eq('user_id', user.id)
        .gte('start_time', `${todayStr}T00:00:00`)
        .lt('start_time', `${todayStr}T23:59:59`)

      const todayMinutes = todayEntries?.reduce((acc, e) => acc + ((e as any).duration_minutes || 0), 0) || 0

      // Get this month's entries for revenue calculation
      const { data: monthEntries } = await supabase
        .from('time_entries')
        .select(`
          duration_minutes,
          projects(hourly_rate)
        `)
        .eq('user_id', user.id)
        .gte('start_time', startOfMonth.toISOString())

      let monthlyRevenue = 0
      monthEntries?.forEach((entry) => {
        const hours = ((entry as any).duration_minutes || 0) / 60
        const project = entry.projects as { hourly_rate: number | null } | null
        const rate = project?.hourly_rate || 0
        monthlyRevenue += hours * rate
      })

      // Get active projects count
      const { count: activeProjects } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .neq('status', 'done')

      // Get week's total hours
      const startOfWeek = new Date(today)
      startOfWeek.setDate(today.getDate() - today.getDay())
      startOfWeek.setHours(0, 0, 0, 0)

      const { data: weekEntries } = await supabase
        .from('time_entries')
        .select('duration')
        .eq('user_id', user.id)
        .gte('start_time', startOfWeek.toISOString())

      const weekMinutes = weekEntries?.reduce((acc, e) => acc + ((e as any).duration_minutes || 0), 0) || 0

      return {
        todayHours: Math.floor(todayMinutes / 60),
        todayMinutes: todayMinutes % 60,
        monthlyRevenue: Math.round(monthlyRevenue),
        activeProjects: activeProjects || 0,
        weekHours: Math.round(weekMinutes / 60),
      }
    },
    enabled: !!user?.id,
  })
}
